/**
 * Annotation strokes, and the geometry that places them on a page.
 *
 * Every viewer of a shared document sees it at a different size — their own
 * window, their own zoom, a phone or a desktop — so a stroke can never be held
 * in screen pixels. It is held relative to the PAGE it was drawn on: x as a
 * fraction of the page's width, y as a fraction of its height, both 0..1. Any
 * viewer multiplies back by the size the page has on their screen and the ink
 * lands on the same word it was drawn over.
 *
 * The line width is a fraction of the page's width for the same reason: it
 * thickens as the page is zoomed, like ink on paper, instead of staying a
 * hairline over a page blown up to three times its size.
 *
 * Drawing uses `viewBox="0 0 1 <ratio>"` (ratio = page height / width) so one
 * user unit is the page's width on both axes — scaling stays uniform and a
 * round pen stays round. Points therefore go out as (x, y * ratio).
 */

/** Freehand ink. */
export type InkTool = 'pen' | 'highlighter'

/** Shapes, dragged from one corner (or end) to the other. */
export type ShapeTool = 'line' | 'arrow' | 'rect' | 'ellipse'

/** What a stamp shows. */
export type StampKind = 'check' | 'cross' | 'star' | 'heart' | 'question'

/** Everything that leaves something on the page. */
export type StrokeTool = InkTool | ShapeTool | 'text' | 'stamp'

/**
 * What the pointer does over a page. `select` leaves it to scroll the
 * document; `laser` points without leaving anything behind.
 */
export type AnnotationTool = 'select' | StrokeTool | 'laser' | 'eraser'

/** What a stroke in progress can be: ink and shapes grow, the laser moves. */
export type LiveTool = InkTool | ShapeTool | 'laser'

export const SHAPE_TOOLS: readonly ShapeTool[] = ['line', 'arrow', 'rect', 'ellipse']
export const STAMP_KINDS: readonly StampKind[] = ['check', 'cross', 'star', 'heart', 'question']

export const isInkTool = (t: unknown): t is InkTool => t === 'pen' || t === 'highlighter'
export const isShapeTool = (t: unknown): t is ShapeTool => SHAPE_TOOLS.includes(t as ShapeTool)

/**
 * One mark on the page — a line of ink, a shape, a piece of text or a stamp.
 *
 * `points` by kind: ink, any number of points along the line; a shape, its two
 * corners (or ends) `[x0, y0, x1, y1]`; text, its top-left corner; a stamp,
 * its centre.
 */
export interface Stroke {
  /** Made by the client that drew it, so it can be undone or erased by id. */
  id: string
  /** Who drew it. */
  by: string
  /** 1-based page of the document; an image is page 1. */
  page: number
  tool: StrokeTool
  /** `#rrggbb`. */
  color: string
  /**
   * A fraction of the page's width: the line width for ink and shapes, the
   * font size for text, the size of a stamp.
   */
  width: number
  /** x0, y0, x1, y1, … — x a fraction of the page's width, y of its height. */
  points: number[]
  /** The words, for `text`. */
  text?: string
  /** Which stamp, for `stamp`. */
  stamp?: StampKind
}

/**
 * A slice of a stroke still being drawn: its points from point `from` on.
 * Sent every few dozen milliseconds while the pen is down, so the room sees
 * the line grow; the finished stroke follows under the same id.
 */
export interface LiveChunk {
  id: string
  page: number
  tool: LiveTool
  color: string
  width: number
  /** Index, in points (pairs), of this slice's first point within the stroke. */
  from: number
  points: number[]
}

/** Where the presenter is in a document: a page, and how far down it (0..1). */
export interface AnnotationView {
  page: number
  offset: number
}

/**
 * Size per tool, as a fraction of the page's width. On an 800px page: a 3px
 * pen, a 14px highlighter, 22px text, a 48px stamp.
 */
export const STROKE_WIDTH: Record<StrokeTool | 'laser', number> = {
  pen: 0.004,
  highlighter: 0.018,
  line: 0.004,
  arrow: 0.004,
  rect: 0.004,
  ellipse: 0.004,
  text: 0.028,
  stamp: 0.06,
  laser: 0.012,
}

/** Longest text annotation, in characters — the server's limit is in bytes, and generous. */
export const MAX_TEXT_LENGTH = 200

/** How see-through a highlighter is, so the text under it stays readable. */
export const HIGHLIGHTER_OPACITY = 0.35

/**
 * Points closer than this to the last one kept are dropped. A pointer reports
 * far more often than a line needs, and every point kept is a point on the
 * wire for every participant.
 */
const MIN_STEP = 0.002

/** Four decimals is a tenth of a pixel on a 1000px page — finer is noise on the wire. */
const round = (n: number) => Math.round(n * 10_000) / 10_000
const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n)

/** A pointer position as a point on the page under it. */
export function toPagePoint(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
): [number, number] {
  if (!rect.width || !rect.height) return [0, 0]
  return [round(clamp01((clientX - rect.left) / rect.width)), round(clamp01((clientY - rect.top) / rect.height))]
}

/**
 * The stroke's points with (x, y) added — unless it is too close to the last
 * point to change the line. Distance is measured with y scaled by the page
 * ratio, so a step means the same length across and down the page.
 */
export function appendPoint(points: number[], x: number, y: number, ratio: number): number[] {
  const n = points.length
  if (n >= 2) {
    const dx = x - points[n - 2]
    const dy = (y - points[n - 1]) * ratio
    if (dx * dx + dy * dy < MIN_STEP * MIN_STEP) return points
  }
  return [...points, x, y]
}

/**
 * An SVG path for the points, in `viewBox="0 0 1 <ratio>"` units.
 *
 * Smoothed through the midpoints of each pair of samples — the line runs
 * through the middle of every segment and curves at each sample — which takes
 * the corners out of a fast stroke without moving it off where it was drawn.
 * A single point (a tap) becomes a dot.
 */
export function strokePath(points: number[], ratio: number): string {
  const n = points.length / 2
  if (n < 1) return ''
  const x = (i: number) => points[i * 2]
  const y = (i: number) => points[i * 2 + 1] * ratio
  const f = (v: number) => +v.toFixed(5)
  if (n === 1) return `M${f(x(0))} ${f(y(0))}l0.0001 0`
  if (n === 2) return `M${f(x(0))} ${f(y(0))}L${f(x(1))} ${f(y(1))}`
  let d = `M${f(x(0))} ${f(y(0))}`
  for (let i = 1; i < n - 1; i++) {
    const mx = (x(i) + x(i + 1)) / 2
    const my = (y(i) + y(i + 1)) / 2
    d += `Q${f(x(i))} ${f(y(i))} ${f(mx)} ${f(my)}`
  }
  return d + `L${f(x(n - 1))} ${f(y(n - 1))}`
}

/** Distance from p to the segment a–b, all in the same units. */
function distanceToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2))
  const cx = ax + t * dx - px
  const cy = ay + t * dy - py
  return Math.sqrt(cx * cx + cy * cy)
}

/**
 * The box a shape's two corners span, in viewBox units (y already scaled).
 * Dragging up or left is as good as down or right.
 */
export function shapeBox(points: number[], ratio: number) {
  const [x0, y0, x1, y1] = points
  return {
    x: Math.min(x0, x1),
    y: Math.min(y0, y1) * ratio,
    w: Math.abs(x1 - x0),
    h: Math.abs(y1 - y0) * ratio,
  }
}

/**
 * The two strokes of an arrowhead at the end of a line, as SVG path data.
 * Sized from the line's width so a thick arrow gets a big head, with a floor
 * so a thin one still reads as an arrow.
 */
export function arrowHead(points: number[], ratio: number, width: number): string {
  const [x0, y0, x1, y1] = [points[0], points[1] * ratio, points[2], points[3] * ratio]
  const angle = Math.atan2(y1 - y0, x1 - x0)
  const len = Math.max(0.02, width * 5)
  const f = (v: number) => +v.toFixed(5)
  const wing = (a: number) => `M${f(x1)} ${f(y1)}L${f(x1 - len * Math.cos(a))} ${f(y1 - len * Math.sin(a))}`
  return wing(angle - Math.PI / 7) + wing(angle + Math.PI / 7)
}

/**
 * The outline of a mark, in viewBox units, as a path of points for the eraser
 * to test against. Text and stamps answer with their box's edge — erasing a
 * word means touching it, not hunting for a pixel of a glyph.
 */
function outline(stroke: Stroke, ratio: number): number[] {
  const p = stroke.points
  if (isInkTool(stroke.tool) || stroke.tool === 'line' || stroke.tool === 'arrow') {
    const out: number[] = []
    for (let i = 0; i + 1 < p.length; i += 2) out.push(p[i], p[i + 1] * ratio)
    return out
  }
  if (stroke.tool === 'rect') {
    const b = shapeBox(p, ratio)
    return [b.x, b.y, b.x + b.w, b.y, b.x + b.w, b.y + b.h, b.x, b.y + b.h, b.x, b.y]
  }
  if (stroke.tool === 'ellipse') {
    const b = shapeBox(p, ratio)
    const out: number[] = []
    for (let i = 0; i <= 32; i++) {
      const a = (i / 32) * Math.PI * 2
      out.push(b.x + b.w / 2 + (b.w / 2) * Math.cos(a), b.y + b.h / 2 + (b.h / 2) * Math.sin(a))
    }
    return out
  }
  return []
}

/** The box a piece of text or a stamp covers, in viewBox units. */
function glyphBox(stroke: Stroke, ratio: number) {
  const [x, y] = stroke.points
  const size = stroke.width
  if (stroke.tool === 'stamp') return { x: x - size / 2, y: y * ratio - size / 2, w: size, h: size }
  // Text has no measured width here; ~0.55em a character is close enough for
  // a sans-serif to aim an eraser at.
  return { x, y: y * ratio, w: Math.max(size, (stroke.text?.length ?? 0) * size * 0.55), h: size * 1.2 }
}

/**
 * Does the eraser at (x, y) touch this mark? `reach` is the eraser's radius
 * as a fraction of the page's width; a line's own thickness counts too, so a
 * fat highlighter is caught at its edge, not only along its centre line.
 */
export function hitsStroke(stroke: Stroke, x: number, y: number, ratio: number, reach: number): boolean {
  const py = y * ratio
  if (stroke.tool === 'text' || stroke.tool === 'stamp') {
    const b = glyphBox(stroke, ratio)
    return x >= b.x - reach && x <= b.x + b.w + reach && py >= b.y - reach && py <= b.y + b.h + reach
  }
  const o = outline(stroke, ratio)
  const limit = reach + stroke.width / 2
  if (o.length === 2) return distanceToSegment(x, py, o[0], o[1], o[0], o[1]) <= limit
  for (let i = 0; i + 3 < o.length; i += 2) {
    if (distanceToSegment(x, py, o[i], o[i + 1], o[i + 2], o[i + 3]) <= limit) return true
  }
  return false
}
